<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" @click.self="close">
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title-wrapper">
            <h5 class="modal-title">Withdrawal History</h5>
          </div>
          <button type="button" class="close-button" @click="close">
            &times;
          </button>
        </div>

        <div class="modal-body-container">
          <!-- Left Column - Stats (33%) -->
          <div class="stats-column" style="overflow-y: auto">
            <div class="modal-stats-vertical">
              <div class="stat-item-vertical">
                <span class="stat-value-vertical">
                  {{ props.claimableBalance.toFixed(2) }} XAP</span
                >
                <span class="stat-label-vertical">
                  <a
                    href="https://claim.exaprotocol.com"
                    target="_blank"
                    class="text-primary text-decoration-underline"
                    >Ready to claim</a
                  ></span
                >
              </div>
              <div class="stat-item-vertical">
                <span class="stat-value-vertical"
                  >{{ getTotalWithdrawn() }} XAP</span
                >
                <span class="stat-label-vertical">Total XAP Withdrawn</span>
              </div>
              <div class="stat-item-vertical">
                <span class="stat-value-vertical">{{
                  withdrawals.length
                }}</span>
                <span class="stat-label-vertical">Total Requests</span>
              </div>
              <div class="stat-item-vertical">
                <span class="stat-value-vertical">{{ getPendingCount() }}</span>
                <span class="stat-label-vertical">Pending</span>
              </div>
            </div>

            <div class="withdrawal-info-box">
              <div class="info-icon">
                <i class="fas fa-info-circle"></i>
              </div>
              <div class="info-content">
                <strong>About Withdrawals</strong>
                <p>
                  Withdrawal requests are processed daily at 12 AM UTC and when
                  sufficient withdrawals are pooled. Pending withdrawals will be
                  processed automatically.
                </p>
              </div>
            </div>
          </div>

          <!-- Right Column - History (67%) -->
          <div class="history-column">
            <div v-if="isLoading" class="loading-indicator">
              <i class="fas fa-circle-notch fa-spin"></i>
              <span>Loading withdrawal history...</span>
            </div>

            <div v-else-if="error" class="error-message">
              <i class="fas fa-exclamation-circle"></i>
              <span>{{ error }}</span>
              <button class="btn btn-link btn-sm" @click="$emit('refresh')">
                Retry
              </button>
            </div>

            <div v-else-if="withdrawals.length === 0" class="no-records">
              <i class="fas fa-info-circle"></i>
              <span>No withdrawal records found.</span>
            </div>

            <div v-else class="withdrawal-records">
              <div class="history-filters">
                <div class="filter-group">
                  <label>Filter by status:</label>
                  <div class="filter-buttons">
                    <button
                      @click="currentFilter = 'all'"
                      :class="[
                        'filter-btn',
                        currentFilter === 'all' ? 'active' : '',
                      ]"
                    >
                      All
                    </button>
                    <button
                      @click="currentFilter = 'pending'"
                      :class="[
                        'filter-btn',
                        currentFilter === 'pending' ? 'active' : '',
                      ]"
                    >
                      Pending
                    </button>
                    <button
                      @click="currentFilter = 'completed'"
                      :class="[
                        'filter-btn',
                        currentFilter === 'completed' ? 'active' : '',
                      ]"
                    >
                      Completed
                    </button>
                  </div>
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-hover table-sm">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Tx Hash</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="withdrawal in filteredWithdrawals"
                      :key="withdrawal._id"
                      :class="getStatusClass(withdrawal.status)"
                    >
                      <td>
                        <div class="withdrawal-date px-2">
                          <span class="date">{{
                            formatDate(withdrawal.createdAt).date
                          }}</span>
                          <span class="time">{{
                            formatDate(withdrawal.createdAt).time
                          }}</span>
                        </div>
                      </td>
                      <td>
                        <span class="amount">{{
                          withdrawal.amount.toFixed(2) + " XAP"
                        }}</span>
                      </td>
                      <td>
                        <span
                          class="status-badge"
                          :class="'status-' + withdrawal.status"
                        >
                          <i :class="getStatusIcon(withdrawal.status)"></i>
                          {{ capitalizeFirst(withdrawal.status) }}
                        </span>
                      </td>
                      <td>
                        <a
                          v-if="withdrawal.transaction?.transactionHash"
                          :href="`${store.state.ETHERSCAN_URL}/tx/${withdrawal.transaction.transactionHash}`"
                          target="_blank"
                          class="btn btn-link btn-sm px-0 py-0"
                        >
                          View <i class="fas fa-external-link-alt ps-1"></i>
                        </a>
                        <span v-else class="text-muted text-xs">
                          Pending...
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            class="refresh-btn"
            @click="$emit('refresh')"
            title="Refresh History"
          >
            <i class="fas fa-sync-alt"></i>
          </button>
          <soft-button
            color="secondary"
            variant="outline"
            size="sm"
            @click="close"
          >
            Close
          </soft-button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed } from "vue";
import { defineProps, defineEmits } from "vue";
import SoftButton from "@/components/SoftButton.vue";
import store from "@/store";

const props = defineProps({
  show: Boolean,
  withdrawals: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
  claimableBalance: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["close", "refresh"]);
const currentFilter = ref("all");

const filteredWithdrawals = computed(() => {
  if (currentFilter.value === "all") {
    return props.withdrawals;
  }
  return props.withdrawals.filter((w) => w.status === currentFilter.value);
});

const close = () => {
  emit("close");
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return {
    date: date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const capitalizeFirst = (str) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const getStatusClass = (status) => {
  switch (status) {
    case "completed":
      return "";
    case "pending":
      return "table-warning";
    case "processing":
      return "table-info";
    case "failed":
      return "table-danger";
    default:
      return "";
  }
};

const getStatusIcon = (status) => {
  switch (status) {
    case "completed":
      return "fas fa-check-circle";
    case "pending":
      return "fas fa-clock";
    case "processing":
      return "fas fa-spinner fa-spin";
    case "failed":
      return "fas fa-times-circle";
    default:
      return "fas fa-question-circle";
  }
};

const getTotalWithdrawn = () => {
  // Sum up all completed withdrawals
  const total = props.withdrawals
    .filter((w) => w.status === "completed")
    .reduce((sum, w) => sum + (w.amount || 0), 0);

  return total.toFixed(2);
};

const getPendingCount = () => {
  return props.withdrawals.filter(
    (w) => w.status === "pending" || w.status === "processing"
  ).length;
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2001;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: white;
  padding: 0;
  border-radius: 0.75rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 95%;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modal-appear 0.3s ease-out;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.modal-body-container {
  display: flex;
  flex-grow: 1;
  min-height: 0;
}

.stats-column {
  width: 33%;
  padding: 1.5rem;
  border-right: 1px solid #e9ecef;
  background-color: rgba(94, 114, 228, 0.02);
  display: flex;
  flex-direction: column;
}

.history-column {
  width: 67%;
  padding: 1.5rem;
  overflow-y: auto;
  max-height: calc(90vh - 180px);
  display: flex;
  flex-direction: column;
}

.modal-stats-vertical {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  margin-bottom: 2rem;
}

.stat-item-vertical {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(94, 114, 228, 0.1);
}

.stat-value-vertical {
  font-size: 1.5rem;
  font-weight: 700;
  color: #5e72e4;
  margin-bottom: 0.25rem;
}

.stat-label-vertical {
  font-size: 0.75rem;
  color: #8392ab;
  text-align: center;
  line-height: 1.3;
}

.modal-title-wrapper {
  display: flex;
  align-items: center;
}

.modal-icon {
  color: #5e72e4;
  font-size: 1.25rem;
  margin-right: 0.75rem;
}

.modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #344767;
  margin-bottom: 0;
}

.close-button {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  transition: color 0.2s;
}

.close-button:hover {
  color: #343a40;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

.refresh-btn {
  background: none;
  border: none;
  color: #5e72e4;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.refresh-btn:hover {
  background-color: rgba(94, 114, 228, 0.1);
}

.loading-indicator,
.error-message,
.no-records {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  text-align: center;
  color: #8392ab;
}

.loading-indicator i,
.error-message i,
.no-records i {
  font-size: 2rem;
  margin-bottom: 1rem;
}

.error-message {
  color: #f5365c;
}

.withdrawal-records {
  width: 100%;
}

.history-filters {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.filter-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-group label {
  font-size: 0.8rem;
  color: #8392ab;
  margin-right: 0.5rem;
  margin-bottom: 0;
}

.filter-buttons {
  display: flex;
  gap: 0.25rem;
}

.filter-btn {
  background: none;
  border: 1px solid #e9ecef;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.7rem;
  color: #6c757d;
  transition: all 0.2s;
}

.filter-btn:hover {
  background-color: #f8f9fa;
}

.filter-btn.active {
  background-color: #5e72e4;
  color: white;
  border-color: #5e72e4;
}

.table {
  margin-bottom: 1rem;
}

.table th {
  font-size: 0.75rem;
  font-weight: 600;
  color: #8392ab;
  text-transform: uppercase;
  padding: 0.75rem;
}

.withdrawal-date {
  display: flex;
  flex-direction: column;
}

.withdrawal-date .date {
  font-size: 0.8rem;
  font-weight: 500;
}

.withdrawal-date .time {
  font-size: 0.7rem;
  color: #8392ab;
}

.amount {
  font-weight: 600;
  font-size: 0.85rem;
}

.currency {
  color: #8392ab;
  font-size: 0.7rem;
  margin-left: 0.2rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.status-completed {
  background-color: rgba(45, 206, 137, 0.1);
  color: #2dce89;
}

.status-pending {
  background-color: rgba(251, 99, 64, 0.1);
  color: #fb6340;
}

.status-processing {
  background-color: rgba(17, 205, 239, 0.1);
  color: #11cdef;
}

.status-failed {
  background-color: rgba(245, 54, 92, 0.1);
  color: #f5365c;
}

.status-badge i {
  margin-right: 0.25rem;
}

.claim-link {
  display: inline-block;
  margin-left: 0.5rem;
  font-size: 0.75rem;
  color: #5e72e4;
  text-decoration: none;
  padding: 0.15rem 0.5rem;
  border-radius: 0.25rem;
  background-color: rgba(94, 114, 228, 0.1);
  transition: all 0.2s;
}

.claim-link:hover {
  background-color: rgba(94, 114, 228, 0.2);
  color: #5e72e4;
  text-decoration: none;
}

.withdrawal-info-box {
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 0.5rem;
  background-color: rgba(94, 114, 228, 0.05);
  display: flex;
  gap: 0.75rem;
}

.info-icon {
  color: #5e72e4;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.info-content strong {
  display: block;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  color: #344767;
}

.info-content p {
  font-size: 0.75rem;
  color: #8392ab;
  margin-bottom: 0;
  line-height: 1.4;
}

.text-emphasis {
  color: #fb6340;
}

.withdrawal-link {
  color: #5e72e4;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s;
}

.withdrawal-link:hover {
  text-decoration: underline;
}

@media (max-width: 768px) {
  .modal-content {
    max-width: 95%;
    width: 95%;
  }

  .modal-body-container {
    flex-direction: column;
  }

  .stats-column {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }

  .history-column {
    width: 100%;
    max-height: calc(90vh - 300px);
  }

  .modal-stats-vertical {
    flex-direction: row;
    gap: 1rem;
    margin-bottom: 1rem;
    overflow-x: auto;
  }

  .stat-item-vertical {
    min-width: 120px;
    padding: 0.75rem;
  }

  .stat-value-vertical {
    font-size: 1.1rem;
  }

  .stat-label-vertical {
    font-size: 0.65rem;
  }
}

/* Styles for the new claimable balance box */
.claimable-balance-box {
  /* background-color: rgba(45, 206, 137, 0.08); Light success green */
  border: 1px dashed rgba(54, 18, 255, 0.2);
  border-radius: 0.5rem;
  padding: 1rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.claimable-balance-header {
  font-size: 1rem;
  font-weight: 600;
  /* color: #2dce89; Success green */
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.claimable-balance-header .fas {
  font-size: 1.1rem;
}

.claimable-balance-amount {
  font-size: 1.75rem;
  font-weight: 700;
  color: #344767; /* Dark text color */
  margin-bottom: 0.5rem;
}

.claimable-balance-amount .currency {
  font-size: 0.9rem;
  font-weight: 500;
  color: #8392ab; /* Muted label color */
}

.claimable-balance-note {
  font-size: 0.75rem;
  color: #8392ab;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.claim-funds-btn-modal {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 1.25rem;
}
</style>
